import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Send, MessageCircle, X, User, Loader2 } from "lucide-react";

const EventChat = ({ eventId, eventTitle, isOpen = true, onClose, inline = false }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if ((isOpen || inline) && eventId) {
      fetchMessages();
      // Poll for new messages every 5 seconds
      pollIntervalRef.current = setInterval(fetchMessages, 5000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, eventId, inline]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/events/${eventId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (err) {
      console.log("Could not fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !token) return;

    setSending(true);
    try {
      await axios.post(`${API}/events/${eventId}/messages`, {
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage("");
      fetchMessages();
    } catch (err) {
      console.log("Could not send message:", err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString();
  };

  if (!isOpen && !inline) return null;

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  const ChatContent = () => (
    <>
      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${inline ? "min-h-[300px] max-h-[400px]" : ""}`}>
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[#7a6200] animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-8">
            <MessageCircle className="w-12 h-12 text-[#52525B] mb-4" />
            <p className="text-[#A1A1AA] font-ubuntu">No messages yet</p>
            <p className="text-[#52525B] text-sm font-ubuntu mt-1">
              Start the conversation!
            </p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="flex items-center gap-2 my-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[#52525B] text-xs font-ubuntu">{date}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              {dateMessages.map((message) => {
                const isOwn = message.user_id === user?.id;
                return (
                  <div
                    key={message.id}
                    className={`flex gap-2 mb-3 ${isOwn ? "flex-row-reverse" : ""}`}
                  >
                    {/* Avatar */}
                    {!isOwn && (
                      <div className="flex-shrink-0">
                        {message.user_avatar ? (
                          <img
                            src={message.user_avatar}
                            alt={message.user_name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-[#7a6200]" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Message bubble */}
                    <div className={`max-w-[75%] ${isOwn ? "items-end" : "items-start"}`}>
                      {!isOwn && (
                        <span className="text-[#7a6200] text-xs font-ubuntu mb-1 block">
                          {message.user_name}
                        </span>
                      )}
                      <div
                        className={`px-4 py-2 rounded-2xl ${
                          isOwn
                            ? "bg-[#7a6200] text-white rounded-br-sm"
                            : "bg-white/10 text-white rounded-bl-sm"
                        }`}
                      >
                        <p className="font-ubuntu text-sm">{message.content}</p>
                      </div>
                      <span className="text-[#52525B] text-xs font-ubuntu mt-1 block">
                        {formatTime(message.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-white/10">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="input-dark flex-1"
            disabled={sending}
            data-testid="chat-message-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="btn-gold"
            data-testid="chat-send-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      </div>
    </>
  );

  // Inline mode - no modal wrapper
  if (inline) {
    return (
      <div className="flex flex-col" data-testid="event-chat-inline">
        <ChatContent />
      </div>
    );
  }

  // Modal mode
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <Card className="w-full max-w-lg h-[600px] flex flex-col bg-[#0A0A0A] border-white/10">
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#7a6200]" />
            </div>
            <div>
              <CardTitle className="font-bebas text-lg tracking-wider text-white">
                EVENT CHAT
              </CardTitle>
              <p className="text-[#52525B] text-xs font-ubuntu truncate max-w-[200px]">
                {eventTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-[#A1A1AA]" />
          </button>
        </CardHeader>

        <ChatContent />
      </Card>
    </div>
  );
};

export default EventChat;
